/**
 * SubcontractorPage — vendors table with KPIs, expiration highlighting,
 * inline add, archive/deleted flows and a page-level Attachments card
 * containing two sample document sections (Sample Subcontractor GL/WC
 * and Sample Indemnity Agreement). Row modal exposes editable fields,
 * per-vendor attachments, change log and notes.
 *
 * This page reuses the existing Insurance visual language + patterns.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MagnifyingGlass, Plus, Archive, Paperclip, ArrowsDownUp, X, Eye, Download, Trash, List as ListIcon, CaretUp, CaretDown, Bell, FileXls, PencilSimple, ArrowClockwise } from "@phosphor-icons/react";
import SubcontractorModal from "@/components/subcontractor/SubcontractorModal";
import AddColumnModal from "@/components/shared/AddColumnModal";
import RestoreColumnsModal from "@/components/shared/RestoreColumnsModal";
import BulkUploadModal from "@/components/shared/BulkUploadModal";
import ReminderConfigModal from "@/components/shared/ReminderConfigModal";

const COLUMNS = [
  { id: "vendor",                    label: "Vendor",                  type: "text" },
  { id: "type",                      label: "Type",                    type: "text" },
  { id: "expiry_date",               label: "Expiry Date",             type: "date" },
  { id: "start_end",                 label: "Start / End Date",        type: "range" },
  { id: "contact",                   label: "Contact",                 type: "text" },
  { id: "phone",                     label: "Phone",                   type: "text" },
  { id: "email_address",             label: "Email Address",           type: "text" },
  { id: "certificate_holder",        label: "Certificate Holder",      type: "text" },
  { id: "indemnity_entity",          label: "Indemnity Entity",        type: "text" },
  { id: "indemnity_signed",          label: "Indemnity Signed",        type: "text" },
  { id: "insurance_contact_name",    label: "Insurance Contact Name",  type: "text" },
  { id: "insurance_contact_phone",   label: "Insurance Contact Phone", type: "text" },
  { id: "insurance_contact_email",   label: "Insurance Contact Email", type: "text" },
];

const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(String(s).length <= 10 ? String(s) + "T00:00:00" : String(s));
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
};
const fmtLU = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d)) return "";
  return `${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}.${String(d.getFullYear()).slice(-2)}`;
};

function expiryTone(iso) {
  if (!iso) return null;
  const d = new Date(String(iso).length <= 10 ? String(iso) + "T00:00:00" : String(iso));
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (diff < 0)  return { cls: "bg-rose-600 text-white",                                 rowCls: "bg-rose-100/70",   label: `${Math.abs(diff)}d overdue` };
  if (diff <=30) return { cls: "bg-rose-100 text-rose-700 border border-rose-200",       rowCls: "bg-rose-50/60",    label: `${diff}d` };
  if (diff <=60) return { cls: "bg-orange-100 text-orange-700 border border-orange-200", rowCls: "bg-orange-50/50",  label: `${diff}d` };
  if (diff <=90) return { cls: "bg-yellow-100 text-yellow-800 border border-yellow-200", rowCls: "bg-yellow-50/40",  label: `${diff}d` };
  return { cls: "text-slate-600", rowCls: "", label: `${diff}d` };
}

export default function SubcontractorPage() {
  const { user } = useAuth();
  const [access, setAccess] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState("");
  const [openVendor, setOpenVendor] = useState(null);
  const [sortBy, setSortBy] = useState({ key: "vendor_id", dir: "asc" });
  const [newRow, setNewRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const [metaAttachments, setMetaAttachments] = useState({ sample_gl_wc: [], sample_indemnity: [] });
  const [uploadingMeta, setUploadingMeta] = useState("");
  const metaFileRefs = useRef({});
  // Hamburger toolbar + modals
  const [toolbarOpen, setToolbarOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [reminderCfgOpen, setReminderCfgOpen] = useState(false);
  const [addColOpen, setAddColOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [reminderMode, setReminderMode] = useState(false);
  const [customCols, setCustomCols] = useState([]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/subcontractors/access"); setAccess(data); }
      catch { setAccess({ can_view: false }); }
    })();
  }, []);

  const load = useCallback(async () => {
    try {
      const [a, d] = await Promise.all([api.get("/subcontractors"), api.get("/subcontractors?deleted=true")]);
      setVendors([...(a.data || []), ...(d.data || [])]);
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  }, []);

  const loadMeta = useCallback(async () => {
    try { const { data } = await api.get("/subcontractors/meta-attachments"); setMetaAttachments(data || { sample_gl_wc: [], sample_indemnity: [] }); }
    catch { /* ignore */ }
  }, []);

  const loadCustomCols = useCallback(async () => {
    try { const { data } = await api.get("/subcontractors/columns"); setCustomCols(Array.isArray(data) ? data : []); }
    catch { setCustomCols([]); }
  }, []);

  useEffect(() => { if (access?.can_view) { load(); loadMeta(); loadCustomCols(); } }, [access?.can_view, load, loadMeta, loadCustomCols]);

  const active = useMemo(() => vendors.filter((v) => !v.archived && !v.deleted), [vendors]);
  const archived = useMemo(() => vendors.filter((v) => v.archived && !v.deleted), [vendors]);
  const deleted = useMemo(() => vendors.filter((v) => v.deleted), [vendors]);

  const filter = useCallback((list) => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((v) => JSON.stringify(v).toLowerCase().includes(q));
  }, [search]);
  const sortList = useCallback((list) => {
    if (!sortBy.key) return list;
    return [...list].sort((a, b) => {
      let av = a[sortBy.key], bv = b[sortBy.key];
      if (av == null || av === "") return 1;
      if (bv == null || bv === "") return -1;
      av = String(av).toLowerCase(); bv = String(bv).toLowerCase();
      return av === bv ? 0 : (av > bv ? (sortBy.dir === "asc" ? 1 : -1) : (sortBy.dir === "asc" ? -1 : 1));
    });
  }, [sortBy]);

  const activeFiltered = useMemo(() => sortList(filter(active)), [active, filter, sortList]);
  const archivedFiltered = useMemo(() => sortList(filter(archived)), [archived, filter, sortList]);
  const deletedFiltered = useMemo(() => sortList(filter(deleted)), [deleted, filter, sortList]);

  const nextExpiration = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return active.filter((v) => v.expiry_date)
      .map((v) => ({ ...v, _d: new Date(v.expiry_date + "T00:00:00") }))
      .filter((v) => !Number.isNaN(v._d) && v._d >= today)
      .sort((a, b) => a._d - b._d)[0] || null;
  }, [active]);

  const startAdd = () => setNewRow({
    vendor: "", type: "", expiry_date: null, start_date: null, end_date: null,
    contact: "", phone: "", email_address: "",
    certificate_holder: "", indemnity_entity: "", indemnity_signed: "",
    insurance_contact_name: "", insurance_contact_phone: "", insurance_contact_email: "",
  });

  const saveNew = async () => {
    if (!newRow) return;
    setSaving(true);
    try {
      const { data } = await api.post("/subcontractors", newRow);
      toast.success(`Vendor #${data.vendor_id} added`);
      setVendors((prev) => [...prev, data]);
      setNewRow(null);
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setSaving(false); }
  };

  const toggleArchive = async (v) => {
    try { const { data } = await api.patch(`/subcontractors/${v.id}`, { field: "archived", value: !v.archived });
      setVendors((prev) => prev.map((x) => x.id === v.id ? data : x));
      toast.success(v.archived ? "Vendor restored" : "Vendor archived");
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };
  const toggleReminderFlag = async (v) => {
    try { const { data } = await api.patch(`/subcontractors/${v.id}`, { field: "email_reminder_enabled", value: !v.email_reminder_enabled });
      setVendors((prev) => prev.map((x) => x.id === v.id ? data : x));
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };
  const softDelete = async (v) => {
    if (!window.confirm(`Move vendor #${v.vendor_id} to Deleted?`)) return;
    try { await api.delete(`/subcontractors/${v.id}`); setVendors((p) => p.map((x) => x.id === v.id ? { ...x, deleted: true } : x)); toast.success("Deleted"); }
    catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };
  const restoreDeleted = async (v) => {
    try { const { data } = await api.patch(`/subcontractors/${v.id}`, { field: "deleted", value: false });
      setVendors((p) => p.map((x) => x.id === v.id ? data : x)); toast.success("Restored"); }
    catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };
  const hardDelete = async (v) => {
    if (!window.confirm(`Purge vendor #${v.vendor_id}?`)) return;
    try { await api.delete(`/subcontractors/${v.id}?hard=true`); setVendors((p) => p.filter((x) => x.id !== v.id)); toast.success("Purged"); }
    catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const uploadMeta = async (section, file) => {
    if (!file) return;
    setUploadingMeta(section);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/subcontractors/meta-attachments?section=${section}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded sample doc`);
      await loadMeta();
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setUploadingMeta(""); }
  };
  const deleteMeta = async (fid) => {
    try { await api.delete(`/subcontractors/meta-attachments/${fid}`); await loadMeta(); }
    catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };
  const backend = process.env.REACT_APP_BACKEND_URL;
  const metaUrl = (fid, dl) => `${backend}/api/subcontractors/meta-attachments/${fid}${dl ? "?download=true" : ""}`;

  if (access === null) return <div className="min-h-screen bg-[#F4F6F8]"><AppHeader /><div className="p-8 text-center text-sm text-slate-500">Loading…</div></div>;
  if (!access?.can_view) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <AppHeader />
      <main className="max-w-none mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10 py-3" data-testid="subcontractor-page">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <h1 className="text-xl font-bold text-slate-900 mr-2" data-testid="subcontractor-title">Subcontractor Insurance</h1>
          <div className="relative flex-1 min-w-0 max-w-[520px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search vendors…" className="pl-9 bg-white" data-testid="subcontractor-search" />
          </div>
          <div className="ml-auto flex items-center gap-1.5 flex-wrap">
            <Button size="sm" onClick={startAdd} className="bg-[#1f64c2] hover:bg-[#1854a3]" data-testid="subcontractor-add">
              <Plus size={13} className="mr-1" /> Add vendor
            </Button>
            <Button size="sm" variant="outline" onClick={() => setToolbarOpen((s) => !s)} data-testid="subcontractor-toolbar-toggle" title="More actions">
              <ListIcon size={14} className="mr-1" />
              {toolbarOpen ? <CaretUp size={11} /> : <CaretDown size={11} />}
            </Button>
          </div>
        </div>

        {toolbarOpen && (
          <div className="mb-3 rounded-lg border border-slate-200 bg-white p-2 flex flex-wrap gap-1.5" data-testid="subcontractor-toolbar-panel">
            <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="subcontractor-bulk-open">
              <FileXls size={14} className="mr-1.5" /> Bulk upload
            </Button>
            <Button size="sm" variant="outline" onClick={startAdd} data-testid="subcontractor-toggle-edit">
              <PencilSimple size={14} className="mr-1.5" /> Add / edit mode
            </Button>
            <Button size="sm" variant={reminderMode ? "default" : "outline"} onClick={() => setReminderMode((m) => !m)}
              data-testid="subcontractor-reminder-mode"
              className={reminderMode ? "bg-amber-500 hover:bg-amber-600" : ""}>
              <Bell size={14} className="mr-1.5" /> {reminderMode ? "Exit reminder mode" : "Reminder flags"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setReminderCfgOpen(true)} data-testid="subcontractor-email-reminders">
              <Bell size={14} className="mr-1.5" /> Email reminders
            </Button>
          </div>
        )}

        {/* KPI cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-3" data-testid="subcontractor-kpis">
          <KpiCard label="Total Vendors" value={active.length.toLocaleString()} testId="kpi-count" />
          <KpiCard
            label="Next Expiration Date"
            value={nextExpiration ? fmtDate(nextExpiration.expiry_date) : "—"}
            secondary={nextExpiration ? `${nextExpiration.vendor || "—"} | ${nextExpiration.type || "—"} | ${fmtDate(nextExpiration.expiry_date)}` : "No upcoming"}
            testId="kpi-next"
          />
          <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 text-[10px]" data-testid="expiration-legend">
            <div className="uppercase tracking-widest text-slate-500 font-semibold mb-1">Expiration Legend</div>
            <div className="flex flex-wrap gap-1">
              <span className="bg-rose-100 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded-full font-semibold">≤30 d</span>
              <span className="bg-orange-100 text-orange-700 border border-orange-200 px-1.5 py-0.5 rounded-full font-semibold">≤60 d</span>
              <span className="bg-yellow-100 text-yellow-800 border border-yellow-200 px-1.5 py-0.5 rounded-full font-semibold">≤90 d</span>
              <span className="bg-rose-600 text-white px-1.5 py-0.5 rounded-full font-semibold">Expired</span>
            </div>
          </div>
          {/* Sample docs attachments KPI card */}
          <div className="rounded-lg bg-white border border-slate-200 px-3 py-2" data-testid="sample-docs-card">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Attachments</div>
            {["sample_gl_wc", "sample_indemnity"].map((sec) => {
              const label = sec === "sample_gl_wc" ? "Sample Subcontractor GL/WC" : "Sample Indemnity Agreement";
              const versions = metaAttachments[sec] || [];
              const current = versions[0];
              const prev = versions.slice(1);
              return (
                <div key={sec} className="mt-1.5">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-bold text-slate-700 truncate" title={label}>{label}</div>
                    <button type="button" onClick={() => metaFileRefs.current[sec]?.click()} disabled={uploadingMeta === sec}
                      className="text-[9px] font-semibold text-[#1f64c2] hover:text-[#327de3] px-1.5 py-0.5 rounded border border-slate-200 bg-white">
                      <Paperclip size={9} className="inline mr-0.5" />{uploadingMeta === sec ? "…" : "Add"}
                    </button>
                    <input ref={(el) => { metaFileRefs.current[sec] = el; }} type="file" className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMeta(sec, f); e.target.value = ""; }} />
                  </div>
                  {!current ? <div className="text-[9px] text-slate-400 italic">No file uploaded.</div> : (
                    <div className="text-[9px] mt-0.5 flex items-center gap-1 bg-slate-50 border border-slate-200 rounded px-1 py-0.5">
                      <span className="font-semibold text-slate-500">v{current.version}</span>
                      <span className="truncate flex-1" title={current.filename}>{current.filename}</span>
                      <a href={metaUrl(current.id)} target="_blank" rel="noreferrer" className="text-[#1f64c2]"><Eye size={9} /></a>
                      <a href={metaUrl(current.id, true)} className="text-[#1f64c2]"><Download size={9} /></a>
                      <button onClick={() => deleteMeta(current.id)} className="text-rose-500"><Trash size={9} /></button>
                    </div>
                  )}
                  {prev.length > 0 && (
                    <details className="mt-0.5">
                      <summary className="text-[9px] uppercase tracking-widest font-semibold text-slate-500 cursor-pointer">Previous ({prev.length})</summary>
                      <div className="mt-0.5 space-y-0.5 pl-2 border-l border-slate-200">
                        {prev.map((v) => (
                          <div key={v.id} className="text-[9px] flex items-center gap-1">
                            <span className="font-semibold text-slate-500">v{v.version}</span>
                            <span className="truncate flex-1" title={v.filename}>{v.filename}</span>
                            <a href={metaUrl(v.id)} target="_blank" rel="noreferrer" className="text-[#1f64c2]"><Eye size={9} /></a>
                            <a href={metaUrl(v.id, true)} className="text-[#1f64c2]"><Download size={9} /></a>
                            <button onClick={() => deleteMeta(v.id)} className="text-rose-500"><Trash size={9} /></button>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <SectionHeader label={`Active vendors (${activeFiltered.length}${search ? ` of ${active.length}` : ""})`} />
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" onClick={() => setAddColOpen(true)} className="text-[11px] h-7 px-2 border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="subcontractor-add-column" title="Add a custom column">
              <Plus size={11} className="mr-1" /> Add column
            </Button>
            <Button size="sm" variant="outline" onClick={() => setRestoreOpen(true)} className="text-[11px] h-7 px-2 border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="subcontractor-restore-columns" title="Restore a previously-deleted column">
              <ArrowClockwise size={11} className="mr-1" /> Restore custom columns
            </Button>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-xs" data-testid="subcontractor-table-active">
            <thead>
              <tr className="bg-slate-50">
                {reminderMode && <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 text-center">Flag</th>}
                <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200">Vendor ID</th>
                <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200">LU</th>
                <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 text-center">Attach.</th>
                {COLUMNS.map((c) => (
                  <th key={c.id} onClick={() => setSortBy((s) => ({ key: c.id, dir: s.key === c.id && s.dir === "asc" ? "desc" : "asc" }))}
                      className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap cursor-pointer hover:text-[#1f64c2]">
                    {c.label} <ArrowsDownUp size={9} className="inline text-slate-300" />
                  </th>
                ))}
                {customCols.map((c) => (
                  <th key={c.id} className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap">{c.label}</th>
                ))}
                <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200">Actions</th>
              </tr>
            </thead>
            <tbody>
              {newRow && (
                <tr className="bg-[#eaf2ff]/40" data-testid="subcontractor-new-row">
                  {reminderMode && <td></td>}
                  <td className="px-2 py-0.5 border-b border-slate-100 text-[11px] font-bold text-slate-500">NEW</td>
                  <td></td><td></td>
                  {COLUMNS.map((c) => (
                    <td key={c.id} className="px-1.5 py-0.5 border-b border-slate-100 min-w-[110px]">
                      {c.id === "start_end" ? (
                        <div className="flex gap-1">
                          <input type="date" value={newRow.start_date || ""} onChange={(e) => setNewRow({ ...newRow, start_date: e.target.value || null })} className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs" />
                          <input type="date" value={newRow.end_date || ""} onChange={(e) => setNewRow({ ...newRow, end_date: e.target.value || null })} className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs" />
                        </div>
                      ) : c.id === "indemnity_signed" ? (
                        <select value={newRow.indemnity_signed || ""} onChange={(e) => setNewRow({ ...newRow, indemnity_signed: e.target.value })} className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs">
                          <option value="">—</option><option>Yes</option><option>No</option>
                        </select>
                      ) : (
                        <input type={c.type === "date" ? "date" : "text"} value={newRow[c.id] ?? ""} onChange={(e) => setNewRow({ ...newRow, [c.id]: e.target.value || null })} className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs" data-testid={`subcontractor-new-${c.id}`} />
                      )}
                    </td>
                  ))}
                  {customCols.map((c) => <td key={c.id}></td>)}
                  <td className="px-2 py-0.5 border-b border-slate-100">
                    <Button size="sm" onClick={saveNew} disabled={saving} className="h-6 px-2 text-[10px] bg-[#327de3] hover:bg-[#1f64c2]" data-testid="subcontractor-new-save">Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setNewRow(null)} className="h-6 px-2 text-[10px]"><X size={11} /></Button>
                  </td>
                </tr>
              )}
              {activeFiltered.map((v) => <VendorRow key={v.id} v={v} customCols={customCols} reminderMode={reminderMode} onToggleReminder={() => toggleReminderFlag(v)} onOpen={() => setOpenVendor(v)} onArchive={() => toggleArchive(v)} />)}
              {activeFiltered.length === 0 && !newRow && (
                <tr><td colSpan={COLUMNS.length + customCols.length + 4 + (reminderMode ? 1 : 0)} className="text-center text-slate-400 italic py-6 text-xs">
                  {search ? "No vendors match." : "No vendors yet. Click Add vendor."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Archived */}
        <div className="mt-4">
          <button onClick={() => setShowArchived((s) => !s)} className="text-[11px] uppercase tracking-widest font-bold text-slate-500 inline-flex items-center gap-1" data-testid="subcontractor-archived-toggle">
            <span className={`inline-block transition-transform ${showArchived ? "rotate-90" : ""}`}>▸</span>
            Archived vendors ({archivedFiltered.length})
          </button>
          {showArchived && (
            <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50/60 overflow-x-auto">
              <table className="w-full text-xs" data-testid="subcontractor-table-archived">
                <tbody>
                  {archivedFiltered.map((v) => (
                    <VendorRow key={v.id} v={v} customCols={customCols} dim onOpen={() => setOpenVendor(v)} onArchive={() => toggleArchive(v)} onDelete={() => softDelete(v)} />
                  ))}
                  {archivedFiltered.length === 0 && <tr><td className="text-center text-slate-400 italic py-4 text-xs">No archived vendors.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-3">
          <button onClick={() => setShowDeleted((s) => !s)} className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 inline-flex items-center gap-1" data-testid="subcontractor-deleted-toggle">
            <span className={`inline-block transition-transform ${showDeleted ? "rotate-90" : ""}`}>▸</span>
            Deleted vendors ({deletedFiltered.length})
          </button>
          {showDeleted && (
            <div className="mt-1 rounded-md border border-slate-200 bg-slate-50/40 overflow-x-auto">
              <table className="w-full text-[10px]">
                <tbody>
                  {deletedFiltered.map((v) => (
                    <tr key={v.id} className="opacity-60">
                      <td className="px-1.5 py-0.5 border-b border-slate-100 font-semibold text-slate-500">#{v.vendor_id}</td>
                      <td className="px-1.5 py-0.5 border-b border-slate-100">{v.vendor}</td>
                      <td className="px-1.5 py-0.5 border-b border-slate-100">
                        <button onClick={() => restoreDeleted(v)} className="text-[9px] font-semibold text-emerald-700 hover:underline mr-1">Restore</button>
                        {user?.is_super_admin && <button onClick={() => hardDelete(v)} className="text-[9px] font-semibold text-rose-600 hover:underline">Purge</button>}
                      </td>
                    </tr>
                  ))}
                  {deletedFiltered.length === 0 && <tr><td className="text-center text-slate-400 italic py-2 text-[10px]">No deleted vendors.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <SubcontractorModal vendor={openVendor} customCols={customCols}
        onClose={() => { setOpenVendor(null); load(); }}
        onChange={(u) => { setVendors((prev) => prev.map((x) => x.id === u.id ? u : x)); setOpenVendor(u); }}
      />

      <AddColumnModal
        open={addColOpen}
        onClose={() => setAddColOpen(false)}
        onCreated={() => loadCustomCols()}
        basePath="/subcontractors"
        defaultLabels={COLUMNS.map((c) => c.label).concat(["Vendor ID", "LU", "Attach.", "Start Date", "End Date"])}
        testIdPrefix="subcontractor-add-column"
      />
      <RestoreColumnsModal
        open={restoreOpen}
        onClose={() => setRestoreOpen(false)}
        onRestored={() => loadCustomCols()}
        basePath="/subcontractors"
        testIdPrefix="subcontractor-restore-columns"
      />
      <BulkUploadModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onImported={() => load()}
        basePath="/subcontractors"
        entityLabel="vendors"
        templateFilename="STRATA SUBCONTRACTOR TEMPLATE.xlsx"
        templateCols={[
          { key: "vendor", label: "Vendor", sample: "ABC Construction" },
          { key: "type", label: "Type", sample: "Framing" },
          { key: "expiry_date", label: "Expiry Date", date: true, sample: "2026-06-30" },
          { key: "start_date", label: "Start Date", date: true },
          { key: "end_date", label: "End Date", date: true },
          { key: "contact", label: "Contact", sample: "Jane Doe" },
          { key: "phone", label: "Phone" },
          { key: "email_address", label: "Email Address" },
          { key: "certificate_holder", label: "Certificate Holder" },
          { key: "indemnity_entity", label: "Indemnity Entity" },
          { key: "indemnity_signed", label: "Indemnity Signed", sample: "Yes" },
          { key: "insurance_contact_name", label: "Insurance Contact Name" },
          { key: "insurance_contact_phone", label: "Insurance Contact Phone" },
          { key: "insurance_contact_email", label: "Insurance Contact Email" },
        ]}
        testIdPrefix="subcontractor-bulk"
      />
      <ReminderConfigModal
        open={reminderCfgOpen}
        onClose={() => setReminderCfgOpen(false)}
        isSuperAdmin={!!user?.is_super_admin}
        basePath="/subcontractors"
        entityLabel="vendors"
        entityCountField="flagged_vendors"
        testIdPrefix="subcontractor-reminder-config"
      />
    </div>
  );
}

function KpiCard({ label, value, secondary, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-lg font-bold text-slate-900 leading-tight mt-0.5" data-testid={`${testId}-value`}>{value}</div>
      {secondary && <div className="text-[10px] text-slate-500 mt-0.5 truncate" title={secondary}>{secondary}</div>}
    </div>
  );
}

function SectionHeader({ label }) {
  return <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 mt-1">{label}</div>;
}

function VendorRow({ v, customCols = [], reminderMode, onToggleReminder, dim, onOpen, onArchive, onDelete }) {
  const tone = expiryTone(v.expiry_date);
  const rowCls = !v.archived && tone ? tone.rowCls : "";
  const cf = v.custom_fields || {};
  return (
    <tr className={`hover:bg-[#eaf2ff]/40 cursor-pointer border-b border-slate-100 ${dim ? "opacity-70" : ""} ${v.archived ? "bg-emerald-50/70" : rowCls}`}
        onClick={onOpen} data-testid={`subcontractor-row-${v.id}`}>
      {reminderMode && (
        <td className="px-2 py-0.5 text-center" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={!!v.email_reminder_enabled} onChange={onToggleReminder}
            className="w-3.5 h-3.5 accent-amber-500" data-testid={`subcontractor-reminder-${v.id}`} />
        </td>
      )}
      <td className="px-2 py-0.5 font-bold text-slate-700 whitespace-nowrap">#{v.vendor_id}</td>
      <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-600">{fmtLU(v.updated_at)}</td>
      <td className="px-2 py-0.5 whitespace-nowrap text-center">
        {v.has_attachments ? <Paperclip size={13} weight="bold" className="inline text-slate-600" /> : <span className="inline-block w-5 h-3 bg-rose-200 border border-rose-300 rounded-sm" />}
      </td>
      {COLUMNS.map((c) => {
        let content;
        if (c.id === "start_end") content = (v.start_date || v.end_date) ? `${fmtDate(v.start_date)} – ${fmtDate(v.end_date)}` : "";
        else if (c.id === "expiry_date") {
          const t = expiryTone(v.expiry_date);
          content = <div className="flex items-center gap-1.5"><span>{fmtDate(v.expiry_date)}</span>{t && <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${t.cls}`}>{t.label}</span>}</div>;
        } else content = v[c.id] || "";
        return <td key={c.id} className="px-2 py-0.5 whitespace-nowrap">{content}</td>;
      })}
      {customCols.map((cc) => (
        <td key={cc.id} className="px-2 py-0.5 whitespace-nowrap text-slate-700">{cf[cc.key] ?? ""}</td>
      ))}
      <td className="px-2 py-0.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1">
          <button onClick={onArchive}
            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${v.archived ? "bg-white border-emerald-300 text-emerald-700 hover:bg-emerald-100" : "bg-white border-slate-200 text-slate-500 hover:border-rose-300 hover:text-rose-600"}`}
            data-testid={`subcontractor-archive-${v.id}`}>
            <Archive size={10} className="inline mr-0.5" />{v.archived ? "Restore" : "Archive"}
          </button>
          {v.archived && onDelete && (
            <button onClick={onDelete} className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-white border-slate-200 text-slate-500 hover:border-rose-400 hover:text-rose-600"
              data-testid={`subcontractor-delete-${v.id}`}>Delete</button>
          )}
        </div>
      </td>
    </tr>
  );
}
